import { BLANK_VALUE } from "./measurement/formatters";
import { buildPermission } from "portal/utils/auth";
import {
  ButtonBase,
  Card,
  CardContent,
  Chip,
  GridTypeMap,
  SvgIcon,
  Typography,
} from "@mui/material";
import { classes } from "portal/theme/theme";
import { CycleSlot } from "portal/utils/metrics";
import { DailyMetricResponse } from "protos/portal/metrics";
import { DateTime } from "luxon";
import {
  getMetricName,
  METRIC_COVERAGE_SPEED,
  METRIC_OVERALL_EFFICIENCY,
} from "portal/utils/certifiedMetrics";
import { isSameDate, isToday } from "portal/utils/dates";
import { JigsawDataSourceIcon } from "./JigsawDataSourceIcon";
import { Measurement } from "./measurement/Measurement";
import { MetricMeasurement } from "./measurement/MetricMeasurement";
import { OverrideProps } from "@mui/material/OverridableComponent";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { PortalJob } from "protos/portal/jobs";
import { useJigsawMetrics } from "../utils/hooks/useJigsawMetrics";
import { useSelf } from "../state/store";
import { useTranslation } from "react-i18next";
import { withAuthorizationRequired } from "./auth/WithAuthorizationRequired";
import AcreageIcon from "@mui/icons-material/StraightenOutlined";
import BandingIcon from "@mui/icons-material/ViewColumnOutlined";
import CropImage from "portal/images/icons/crop.svg?react";
import Grid from "@mui/material/Grid";
import React, { FunctionComponent } from "react";
import ThinningIcon from "@mui/icons-material/FlakyOutlined";

const SUMMARY_METRICS = [METRIC_OVERALL_EFFICIENCY, METRIC_COVERAGE_SPEED];

// We shoehorn days into jobs. This type adds an explicit flag to distinguish
// the two.
export type TaggedJob = TrueJob | DayJob;
type TrueJob = PortalJob & { isDay: false };
type DayJob = Partial<PortalJob> &
  Pick<PortalJob, "name" | "timestampMs"> & { isDay: true };

export const isJob = (job: TaggedJob): job is TrueJob => !job.isDay;
export const isDay = (job: TaggedJob): job is DayJob => job.isDay;

const hasThinning = (job: TaggedJob): boolean =>
  (job.metrics?.thinnedCrops ?? 0) > 0;

interface JobSummaryProps {
  className?: string;
  job: TaggedJob;
  metrics?: DailyMetricResponse;
  onClick?: () => void;
  showFullMetrics?: boolean;
}

const _JobSummary: FunctionComponent<JobSummaryProps> = ({
  className,
  job,
  onClick,
  metrics,
  showFullMetrics = false,
}) => {
  const { isInternal } = useSelf();
  const { t, i18n } = useTranslation();

  const useJigsaw = useJigsawMetrics();

  const chipProps: OverrideProps<GridTypeMap<object, "div">, "div"> = {
    size: { xs: 6 },
    className: classes(
      "flex items-center gap-1",
      "whitespace-nowrap ellipsis overflow-hidden"
    ),
  };
  const bigChipProps = { ...chipProps, size: { xs: 12 } };
  const iconProps = { fontSize: "small" } as const;

  const startTime = DateTime.fromMillis(job.timestampMs);
  const endTime = job.stopTimestampMs
    ? DateTime.fromMillis(job.stopTimestampMs)
    : undefined;
  let formattedTime;
  if (isDay(job)) {
    formattedTime = startTime.toLocaleString(
      { month: "numeric", day: "numeric" },
      {
        locale: i18n.language,
      }
    );
  } else if (!endTime && isToday(startTime)) {
    formattedTime = t("components.JobSummary.singleDay", {
      date: startTime.toLocaleString(
        { month: "numeric", day: "numeric" },
        {
          locale: i18n.language,
        }
      ),
      startTime: startTime.toLocaleString(
        { hour: "numeric", minute: "2-digit" },
        {
          locale: i18n.language,
        }
      ),
      endTime: "___",
    });
  } else if (!endTime) {
    formattedTime = t("components.JobSummary.multiDay", {
      startDate: startTime.toLocaleString(
        { month: "numeric", day: "numeric" },
        {
          locale: i18n.language,
        }
      ),
      endDate: "___",
    });
  } else if (isSameDate(startTime, endTime)) {
    formattedTime = t("components.JobSummary.singleDay", {
      date: startTime.toLocaleString(
        { month: "numeric", day: "numeric" },
        {
          locale: i18n.language,
        }
      ),
      startTime: startTime.toLocaleString(
        { hour: "numeric", minute: "2-digit" },
        {
          locale: i18n.language,
        }
      ),
      endTime: endTime.toLocaleString(
        { hour: "numeric", minute: "2-digit" },
        {
          locale: i18n.language,
        }
      ),
    });
  } else {
    formattedTime = t("components.JobSummary.multiDay", {
      startDate: startTime.toLocaleString(
        { month: "numeric", day: "numeric" },
        {
          locale: i18n.language,
        }
      ),
      endDate: endTime.toLocaleString(
        { month: "numeric", day: "numeric" },
        {
          locale: i18n.language,
        }
      ),
    });
  }

  return (
    <Card className={className}>
      <ButtonBase
        className="w-full"
        onClick={() => onClick?.()}
        focusVisibleClassName="border-solid border-2 border-white"
      >
        <CardContent className="flex flex-col w-full gap-2">
          <div className="flex items-baseline justify-between">
            <Typography
              variant="h2"
              className={classes("text-xl font-poppins font-bold normal-case")}
            >
              {job.name}
            </Typography>
            <span className="font-normal opacity-60">{formattedTime}</span>
          </div>
          {isJob(job) && (
            <Grid
              container
              rowSpacing={1}
              columnSpacing={0}
              className={classes(
                "flex justify-between items-center text-sm w-full font-poppins"
              )}
            >
              <Grid {...chipProps}>
                <SvgIcon {...iconProps}>
                  <CropImage />
                </SvgIcon>
                <span>
                  {job.metrics?.latestCrop?.localizedName ?? BLANK_VALUE}
                </span>
              </Grid>
              <Grid
                {...chipProps}
                className={classes(chipProps.className, "flex justify-end")}
              >
                <AcreageIcon {...iconProps} />
                <Measurement
                  cycleSlot={CycleSlot.AREA}
                  label="long"
                  unit="ac"
                  value={job.metrics?.acresWeeded}
                />
              </Grid>
              {showFullMetrics && (
                <>
                  <Grid {...bigChipProps}>
                    <BandingIcon {...iconProps} />
                    {t("components.robots.RobotSummary.banding.withName", {
                      name:
                        job.bandingProfile || t("utils.descriptors.unknown"),
                    })}
                  </Grid>
                  <Grid {...bigChipProps}>
                    <ThinningIcon {...iconProps} />
                    {t("components.robots.RobotSummary.thinning.withName", {
                      name: hasThinning(job)
                        ? job.thinningProfile || t("utils.descriptors.unknown")
                        : BLANK_VALUE,
                    })}
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {metrics && (
            <div className="mt-1 flex flex-col gap-2 text-left text-sm">
              {SUMMARY_METRICS.map((metric) => {
                return (
                  <Grid
                    key={metric.id}
                    className="flex justify-between items-end"
                    size={12}
                  >
                    <span className="flex-1 text-sm/4">
                      {getMetricName(t, metric)}
                    </span>
                    <MetricMeasurement data={metrics} metric={metric} />
                  </Grid>
                );
              })}
              {isInternal && useJigsaw && job.metrics?.dataSource ? (
                <Grid className="flex justify-between items-end">
                  <span>{t("components.JobSummary.dataSource")}</span>
                  <Chip
                    icon={
                      <JigsawDataSourceIcon
                        dataSource={job.metrics.dataSource}
                      />
                    }
                    label={job.minSoftwareVersion || BLANK_VALUE}
                    size="small"
                  />
                </Grid>
              ) : undefined}
            </div>
          )}
        </CardContent>
      </ButtonBase>
    </Card>
  );
};

export const JobSummary = withAuthorizationRequired(
  {
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.jobs,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.jobs,
        PermissionDomain.all
      ),
    ],
  },
  _JobSummary
);
